/* eslint-disable max-len */
// src/components/contentHub/ContentWorkflow.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { auth, db, functions } from '../../firebaseConfig';
import { httpsCallable } from "firebase/functions";
import { FiLoader, FiAlertTriangle, FiInfo, FiArrowLeft, FiTrendingUp } from 'react-icons/fi';

// Import all child components
import WorkflowTypeSelector from './WorkflowTypeSelector';
import InspirationSelector from './InspirationSelector';
import ClarificationStep from './ClarificationStep';
import PreGenerationAdjustments from './PreGenerationAdjustments';
import ContentPlanner from './ContentPlanner';
import PostPreviewer from './PostPreviewer';
import VisualGenerator from './VisualGenerator';
import Scheduler from './Scheduler';
import WorkflowStepper from './WorkflowStepper';

import { useToast } from '../shared/contexts/ToastContext';
import { STEP_TITLES, WORKFLOW_STEP_LIST } from '../shared/constants/workflowCopy';

// Cloud Function Definitions
const generateClarifyingQuestionsFn = httpsCallable(functions, 'generateClarifyingQuestions');
const generateRefinedInspirationDescriptionFn = httpsCallable(functions, 'generateRefinedInspirationDescription');
const generateContentPlanFn = httpsCallable(functions, 'generateContentPlan');
const generateFinalPostContentFn = httpsCallable(functions, 'generateFinalPostContent');
const generateImagePromptFn = httpsCallable(functions, 'generateImagePrompt');
const improvePostElementFn = httpsCallable(functions, 'improvePostElement');
const refreshContentPillarsFn = httpsCallable(functions, 'refreshContentPillars');
const saveWorkflowDraftFn = httpsCallable(functions, 'saveWorkflowDraft');
const deleteWorkflowDraftFn = httpsCallable(functions, 'deleteWorkflowDraft');
const getWorkflowDraftsFn = httpsCallable(functions, 'getWorkflowDrafts');

// Workflow step keys — used for routing and stepper
const WORKFLOW_STEPS = {
    MISSION_BRIEFING: 'mission_briefing',
    SPARK: 'spark',
    CLARIFICATION: 'clarification',
    FINE_TUNE: 'fine_tune',
    PLAN: 'plan',
    PREVIEW: 'preview',
    VISUALS: 'visuals',
    PUBLISH: 'publish',
};

// Build display number from the visible stepper (matches stepper numbering)
function getStepDisplayNumber(stepKey, workflowType) {
    const visible = workflowType === 'single'
        ? WORKFLOW_STEP_LIST.filter(s => !s.streamOnly)
        : WORKFLOW_STEP_LIST;
    const idx = visible.findIndex(s => s.key === stepKey);
    return idx >= 0 ? String(idx + 1).padStart(2, '0') : '00';
}

const STEP_TITLE_MAP = {
    [WORKFLOW_STEPS.MISSION_BRIEFING]: STEP_TITLES.MISSION_BRIEFING,
    [WORKFLOW_STEPS.SPARK]: STEP_TITLES.SPARK,
    [WORKFLOW_STEPS.CLARIFICATION]: STEP_TITLES.CLARIFICATION,
    [WORKFLOW_STEPS.FINE_TUNE]: STEP_TITLES.FINE_TUNE,
    [WORKFLOW_STEPS.PLAN]: STEP_TITLES.PLAN,
    [WORKFLOW_STEPS.PREVIEW]: STEP_TITLES.PREVIEW,
    [WORKFLOW_STEPS.VISUALS]: STEP_TITLES.VISUALS,
    [WORKFLOW_STEPS.PUBLISH]: STEP_TITLES.PUBLISH,
};

function generateShortId() {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let part1 = '';
  let part2 = '';
  for (let i = 0; i < 3; i++) {
    part1 += chars.charAt(Math.floor(Math.random() * chars.length));
    part2 += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return `${part1}-${part2}`;
}

// --- HELPER COMPONENT: Persistent Header with "Big Number" Typography ---
const WorkflowHeader = ({ project, currentStepTitle, currentStepNumber, onExit }) => (
    <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-white/5 pb-8">
        <div className="flex items-start gap-6">
            <button
                onClick={onExit}
                className="mt-2 p-3 text-cream/40 hover:text-cream rounded-full hover:bg-white/5 transition-colors border border-transparent hover:border-warm-border"
                title="Exit to Project Overview"
            >
                <FiArrowLeft className="h-6 w-6" />
            </button>

            <div className="relative">
                 {/* Big Beautiful Number */}
                 <div className="absolute -top-10 -left-12 text-9xl font-black text-white/5 select-none pointer-events-none z-0">
                     {currentStepNumber}
                 </div>

                <h1 className="relative z-10 text-5xl font-black text-cream uppercase tracking-tighter drop-shadow-lg">
                    Content <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream pr-2">Hub</span>
                </h1>
                <p className="text-cream/50 mt-2 font-medium text-lg flex items-center">
                    {currentStepTitle} <span className="mx-2 text-slate-blue">•</span> {project?.projectName}
                </p>
            </div>
        </div>
    </div>
);

function ContentWorkflow({ projectId, resumeDraftId }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { addToast } = useToast();
    const userId = auth.currentUser?.uid;

    // Pre-selected trend from Trends page navigation
    const selectedTrend = location.state?.selectedTrend || null;

    // Core State
    const [currentStep, setCurrentStep] = useState(WORKFLOW_STEPS.MISSION_BRIEFING);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [infoMessage, setInfoMessage] = useState('');

    // Project-Specific State
    const [project, setProject] = useState(null);
    const [availablePillars, setAvailablePillars] = useState([]);

    // Workflow & Data State
    const [workflowConfig, setWorkflowConfig] = useState(null);
    const [userSpark, setUserSpark] = useState('');
    const [inspirationType, setInspirationType] = useState('');
    const [clarifyingQuestions, setClarifyingQuestions] = useState([]);
    const [creativeBrief, setCreativeBrief] = useState('');
    const [adjustments, setAdjustments] = useState({});
    const [contentPlan, setContentPlan] = useState(null);
    const [contentPlanId, setContentPlanId] = useState(null);
    const [provisionalStreamId, setProvisionalStreamId] = useState(null);
    // eslint-disable-next-line
    const [masterStyle, setMasterStyle] = useState({});

    // Post-Specific State
    const [posts, setPosts] = useState([]);

    // Draft State
    const [draftId, setDraftId] = useState(resumeDraftId || null);
    const draftSaveInFlight = useRef(false);

    const goToStep = useCallback((step) => {
        setError('');
        setInfoMessage('');
        setCurrentStep(step);
    }, []);

    // --- Auto-save workflow draft (fire-and-forget, never blocks UI) ---
    const saveDraft = useCallback(async (nextStep, extraData = {}) => {
        if (!projectId || draftSaveInFlight.current) return;
        draftSaveInFlight.current = true;

        try {
            const payload = {
                projectId,
                draftId: draftId || undefined,
                currentStep: nextStep,
                ...extraData,
            };

            const result = await saveWorkflowDraftFn(payload);
            if (result.data?.success && result.data.draftId && !draftId) {
                setDraftId(result.data.draftId);
            }
        } catch (err) {
            // Silent fail — auto-save should never interrupt the user
            console.warn('Auto-save draft failed:', err);
        } finally {
            draftSaveInFlight.current = false;
        }
    }, [projectId, draftId]);

    // --- Delete draft on workflow completion ---
    const cleanupDraft = useCallback(async () => {
        if (!draftId || !projectId) return;
        try {
            await deleteWorkflowDraftFn({ projectId, draftId });
        } catch (err) {
            console.warn('Draft cleanup failed:', err);
        }
    }, [draftId, projectId]);

    // --- Resume from draft on mount ---
    useEffect(() => {
        if (!resumeDraftId || !projectId || !userId) return;

        let cancelled = false;
        const hydrateDraft = async () => {
            try {
                const result = await getWorkflowDraftsFn({ projectId });
                if (cancelled) return;

                const draft = result.data?.drafts?.find(d => d.id === resumeDraftId);
                if (!draft) {
                    addToast('Draft not found. Starting fresh.', 'info');
                    return;
                }

                // Hydrate state from draft
                if (draft.workflowConfig) setWorkflowConfig(draft.workflowConfig);
                if (draft.inspiration?.userSpark) setUserSpark(draft.inspiration.userSpark);
                if (draft.inspiration?.refinedDescription) setCreativeBrief(draft.inspiration.refinedDescription);
                if (draft.clarificationAnswers) setClarifyingQuestions(draft.clarificationAnswers);
                if (draft.adjustments) setAdjustments(draft.adjustments);
                if (draft.contentPlanId) {
                    setContentPlanId(draft.contentPlanId);
                    // Fetch full contentPlan object from Firestore for resume
                    try {
                        const planSnap = await getDoc(doc(db, 'contentPlans', draft.contentPlanId));
                        if (planSnap.exists()) {
                            const planData = planSnap.data();
                            setContentPlan({
                                ...planData.plan,
                                contentPlanId: draft.contentPlanId,
                                creativeBrief: planData.creativeBrief || '',
                                settings: planData.settings || {},
                            });
                            if (Array.isArray(planData.initialSubjects) && planData.initialSubjects.length > 0) {
                                const streamId = generateShortId();
                                setProvisionalStreamId(streamId);
                                setPosts(planData.initialSubjects.map((subject, i) => ({
                                    id: `${streamId}_${Date.now() + i}`,
                                    subject, content: null, visual: null
                                })));
                            }
                        }
                    } catch (planErr) {
                        console.warn('Failed to fetch content plan for draft resume:', planErr);
                    }
                }
                if (draft.currentStep) setCurrentStep(draft.currentStep);

                addToast('Resuming your draft...', 'success');

                // Clean the URL query param
                const url = new URL(window.location);
                url.searchParams.delete('draft');
                window.history.replaceState({}, '', url);
            } catch (err) {
                console.warn('Failed to hydrate draft:', err);
                addToast('Could not resume draft. Starting fresh.', 'info');
            }
        };

        hydrateDraft();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resumeDraftId, projectId, userId]);

    // --- beforeunload: auto-save when user closes tab ---
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (workflowConfig && currentStep !== WORKFLOW_STEPS.MISSION_BRIEFING) {
                // Use sendBeacon for reliable save on tab close
                // Note: This is a best-effort save — may not always succeed
                saveDraft(currentStep);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [workflowConfig, currentStep, saveDraft]);

    // --- Merge workflowConfig into adjustments for backend calls ---
    const mergedAdjustments = useMemo(() => {
        if (!workflowConfig) return adjustments;
        return {
            ...adjustments,
            tone: adjustments?.tone || workflowConfig.tone,
            longevity: adjustments?.longevity ?? workflowConfig.longevity,
            platforms: workflowConfig.platforms,
            templates: workflowConfig.templates,
            mediaType: workflowConfig.mediaType,
            isVertical: workflowConfig.isVertical,
            isEphemeral: workflowConfig.isEphemeral,
        };
    }, [workflowConfig, adjustments]);

    useEffect(() => {
        if (!userId || !projectId) {
            setError("Authentication or Project ID is missing.");
            setLoading(false);
            return;
        }
        const projectRef = doc(db, "projects", projectId);
        getDoc(projectRef).then(docSnap => {
            if (docSnap.exists() && docSnap.data().userId === userId) {
                const projectData = { id: docSnap.id, ...docSnap.data() };
                setProject(projectData);
                setAvailablePillars(Array.isArray(projectData.aiSuggestedContentPillars) ? projectData.aiSuggestedContentPillars.filter(p => p && (typeof p === 'string' || (typeof p === 'object' && p.name))) : []);
            } else {
                setError("Project not found or access denied.");
            }
        }).catch(err => {
            setError("Failed to load project data.");
        }).finally(() => setLoading(false));
    }, [projectId, userId]);

    // --- Refresh Pillars Logic ---
    const handleRefreshPillars = useCallback(async (currentPillars) => {
        if (!projectId) return;
        try {
            // Convert structured pillars to name strings for backend compatibility
            const pillarNames = (currentPillars || []).map(p => (typeof p === 'object' && p.name) ? p.name : String(p));
            const result = await refreshContentPillarsFn({
                projectId,
                existingPillars: pillarNames
            });

            if (result.data.success && result.data.newPillars) {
                setAvailablePillars(result.data.newPillars);
            } else {
                throw new Error("AI did not return new pillars.");
            }
        } catch (err) {
            setError("Failed to refresh content pillars.");
            addToast('Failed to refresh content pillars.', 'error');
        }
    }, [projectId, addToast]);

    const handleWorkflowConfigSelect = (config) => {
        setWorkflowConfig(config);
        goToStep(WORKFLOW_STEPS.SPARK);

        // Auto-save: workflow config captured
        const formatLabel = config.format || 'Post';
        const platformLabel = config.platforms?.join(' & ') || '';
        const title = `${formatLabel} \u00B7 ${platformLabel} \u2014 ${new Date().toLocaleDateString()}`;
        saveDraft(WORKFLOW_STEPS.SPARK, { workflowConfig: config, title });
    };

    // --- Shared Helper for Brief Generation ---
    const handleBriefGeneration = async (spark, type, answers) => {
        if (!projectId) { setError("A project must be selected."); return; }
        setLoading(true);
        try {
            const result = await generateRefinedInspirationDescriptionFn({
                projectId,
                initialInspiration: spark,
                inspirationType: type,
                userAnswers: answers,
                format: workflowConfig?.format,
                tone: workflowConfig?.tone, // Pass tone override for brief generation
                platforms: workflowConfig?.platforms, // Pass platforms for LinkedIn-aware briefs
            });
            if (result.data?.success && result.data.refinedDescription) {
                setCreativeBrief(result.data.refinedDescription);
                goToStep(WORKFLOW_STEPS.FINE_TUNE);
                // Auto-save: brief generated
                saveDraft(WORKFLOW_STEPS.FINE_TUNE, { inspiration: { userSpark: spark, refinedDescription: result.data.refinedDescription } });
            } else { throw new Error(result.data?.error || "The AI failed to generate a creative brief."); }
        } catch (err) {
            setError("Failed to generate creative brief. Please try again.");
            addToast('Failed to generate creative brief.', 'error');
            goToStep(WORKFLOW_STEPS.SPARK);
        } finally { setLoading(false); }
    };

    // --- Standard Inspiration Submit Handler ---
    const handleInspirationSubmit = async (spark, type) => {
        if (!projectId) { setError("A project must be selected."); return; }
        setUserSpark(spark);
        setInspirationType(type);

        // Auto-save: inspiration captured
        saveDraft(
            workflowConfig.workflow === 'stream' ? WORKFLOW_STEPS.CLARIFICATION : WORKFLOW_STEPS.FINE_TUNE,
            { inspiration: { userSpark: spark } },
        );

        setLoading(true);
        if (workflowConfig.workflow === 'stream') {
            try {
                const result = await generateClarifyingQuestionsFn({
                    projectId,
                    initialInspiration: spark,
                    format: workflowConfig?.format, // NEW: pass format for format-aware questions
                    platforms: workflowConfig?.platforms, // Pass platforms for LinkedIn-aware questions
                });
                if (result.data?.success && result.data.clarifyingQuestions?.length > 0) {
                    setClarifyingQuestions(result.data.clarifyingQuestions);
                    goToStep(WORKFLOW_STEPS.CLARIFICATION);
                } else {
                    await handleBriefGeneration(spark, type, []);
                }
            } catch (err) { setError("Failed to generate clarifying questions. Please try again."); addToast('Failed to generate clarifying questions.', 'error'); }
        } else {
            await handleBriefGeneration(spark, type, []);
        }
        setLoading(false);
    };

    const handleAdjustmentsConfirm = async (confirmedAdjustments, editedBrief) => {
        if (!projectId) { setError("Project data is missing."); return; }
        // Update creative brief if user edited it in Brief Review
        if (editedBrief && editedBrief !== creativeBrief) {
            setCreativeBrief(editedBrief);
        }
        const finalBrief = editedBrief || creativeBrief;
        // Store slim adjustments (tone + longevity from workflowConfig)
        setAdjustments(confirmedAdjustments);

        // Auto-save: adjustments captured
        saveDraft(
            workflowConfig.workflow === 'stream' ? WORKFLOW_STEPS.PLAN : WORKFLOW_STEPS.PREVIEW,
            { adjustments: confirmedAdjustments },
        );

        setLoading(true);

        // Build merged adjustments for backend (includes platform/template from workflowConfig)
        const backendAdjustments = {
            ...confirmedAdjustments,
            platforms: workflowConfig.platforms,
            templates: workflowConfig.templates,
            mediaType: workflowConfig.mediaType,
            isVertical: workflowConfig.isVertical,
            isEphemeral: workflowConfig.isEphemeral,
        };

        if (workflowConfig.workflow === 'stream') {
            try {
                const payload = { projectId, userSpark, refinedInspirationDescription: finalBrief, contentType: workflowConfig.type, frequency: workflowConfig.frequency, count: workflowConfig.count, adjustments: backendAdjustments };
                const result = await generateContentPlanFn(payload);
                if (result.data?.success && result.data.contentPlan) {
                    const fullContentPlan = { ...result.data.contentPlan, contentPlanId: result.data.contentPlanId, creativeBrief: finalBrief, settings: { contentType: workflowConfig.type, ...backendAdjustments } };
                    setContentPlan(fullContentPlan);
                    setContentPlanId(result.data.contentPlanId);

                    const newProvisionalId = generateShortId();
                    setProvisionalStreamId(newProvisionalId);

                    setPosts(result.data.postSubjects.map((subject, i) => ({
                        id: `${newProvisionalId}_${Date.now() + i}`,
                        subject, content: null, visual: null
                    })));
                    goToStep(WORKFLOW_STEPS.PLAN);
                    // Auto-save: plan generated
                    saveDraft(WORKFLOW_STEPS.PLAN, { contentPlanId: result.data.contentPlanId });
                } else { throw new Error(result.data?.error || "Failed to generate content plan."); }
            } catch (err) { setError("Failed to generate content plan. Please try again."); addToast('Failed to generate content plan.', 'error'); }
        } else {
            const singlePostSubject = finalBrief.substring(0, 80) + (finalBrief.length > 80 ? '...' : '');
            setPosts([{ id: generateShortId(), subject: singlePostSubject, content: null, visual: null }]);
            goToStep(WORKFLOW_STEPS.PREVIEW);
        }
        setLoading(false);
    };

    const handlePlanConfirmed = useCallback((finalSubjects) => {
        setPosts(finalSubjects.map((subject, i) => ({
            id: `${provisionalStreamId}_${Date.now() + i}`,
            subject,
            content: null,
            visual: null,
        })));
        goToStep(WORKFLOW_STEPS.PREVIEW);
    }, [provisionalStreamId, goToStep]);

    const handleGenerateFinalContent = useCallback(async (subjectsToGenerate) => {
        if (!projectId) { setError("Missing project data."); return; }
        setLoading(true);
        try {
            const payload = { projectId, contentType: workflowConfig.workflow, postSubjects: subjectsToGenerate, adjustments: mergedAdjustments, userSpark, creativeBrief, contentPlanId };
            const result = await generateFinalPostContentFn(payload);
            if (result.data?.success && Array.isArray(result.data.generatedPosts)) {
                const generatedContents = result.data.generatedPosts;
                let updatedPosts = [];

                // Update posts with generated content
                setPosts(prev => {
                    let contentIndex = 0;
                    const newPosts = prev.map(p => {
                        if (subjectsToGenerate.includes(p.subject)) {
                            const aiResult = generatedContents[contentIndex++];
                            return {
                                ...p,
                                subject: aiResult.subject || p.subject,
                                content: aiResult.content || "AI failed.",
                                visualSequence: aiResult.visualSequence || null
                            };
                        }
                        return p;
                    });
                    updatedPosts = newPosts;
                    return newPosts;
                });

                // Auto-blueprint generation for carousel
                const isCarousel = mergedAdjustments?.platforms?.some(platform =>
                    mergedAdjustments?.templates?.[platform]?.includes('carousel')
                );

                if (isCarousel && updatedPosts.length > 0) {
                    setInfoMessage('Generating carousel blueprints...');

                    try {
                        const postIndex = updatedPosts.findIndex(p => subjectsToGenerate.includes(p.subject));
                        if (postIndex !== -1) {
                            const targetPost = updatedPosts[postIndex];
                            const platforms = mergedAdjustments.platforms || [];

                            const blueprintPromises = platforms
                                .filter(platform => mergedAdjustments?.templates?.[platform]?.includes('carousel'))
                                .map(platform =>
                                    generateImagePromptFn({
                                        projectId,
                                        postId: targetPost.id,
                                        carouselPlatform: platform,
                                        visualSettings: {}
                                    })
                                );

                            const blueprintResults = await Promise.all(blueprintPromises);

                            const allSuccess = blueprintResults.every(r => r.data?.success);
                            if (allSuccess) {
                                setInfoMessage('Carousel blueprints ready!');

                                setPosts(prev => prev.map((p, i) => {
                                    if (i === postIndex) {
                                        const visualSequence = {};
                                        blueprintResults.forEach((result, idx) => {
                                            const platform = platforms.filter(pl =>
                                                mergedAdjustments?.templates?.[pl]?.includes('carousel')
                                            )[idx];
                                            if (result.data?.sequence) {
                                                visualSequence[platform] = result.data.sequence;
                                            }
                                        });
                                        return {
                                            ...p,
                                            visualSequence,
                                            template: 'carousel'
                                        };
                                    }
                                    return p;
                                }));
                            }
                        }
                    } catch (blueprintError) {
                        console.warn('Auto-blueprint generation failed:', blueprintError);
                        setInfoMessage('Blueprints will be generated in Visual Studio');
                    }
                }

                setInfoMessage('');
            } else { throw new Error(result.data?.error || "Failed to generate post content."); }
        } catch (err) { setError("Failed to generate post content. Please try again."); addToast('Failed to generate post content.', 'error'); }
        finally { setLoading(false); }
    }, [projectId, workflowConfig, mergedAdjustments, userSpark, creativeBrief, contentPlanId, setInfoMessage, addToast]);

    const handleEditPostText = useCallback((postIndex, newContent, variantKey = null) => {
        setPosts(prev => prev.map((p, i) => {
            if (i !== postIndex) return p;
            if (variantKey && typeof p.content === 'object' && p.content !== null) {
                return { ...p, content: { ...p.content, [variantKey]: newContent } };
            }
            return { ...p, content: newContent };
        }));
    }, []);

    const handleSetPostVisual = useCallback((postIndex, visualData, platformKey, newStatus, newSequence) => {
    setPosts(prev => prev.map((p, i) => {
        if (i !== postIndex) return p;

        const updatedPost = { ...p };

        if (visualData !== null && visualData !== undefined) {
            if (platformKey) {
                const currentVisuals = p.visual && typeof p.visual === 'object' && !p.visual.url ? p.visual : {};
                updatedPost.visual = { ...currentVisuals, [platformKey]: visualData };
            } else {
                updatedPost.visual = visualData;
            }
        } else if (visualData === null && platformKey) {
            const currentVisuals = p.visual && typeof p.visual === 'object' && !p.visual.url ? p.visual : {};
            const { [platformKey]: removed, ...remainingVisuals } = currentVisuals;
            updatedPost.visual = Object.keys(remainingVisuals).length > 0 ? remainingVisuals : null;
        }

        if (newStatus) {
            updatedPost.visualStatus = newStatus;
        }

        if (newSequence) {
             updatedPost.visualSequence = newSequence;
        }

        return updatedPost;
    }));
}, []);

    const handleSetPostPrompt = useCallback((postIndex, promptText) => {
        setPosts(prev => prev.map((p, i) => {
            if (i === postIndex) {
                return { ...p, visualPrompt: promptText };
            }
            return p;
        }));
    }, []);

    // --- UPDATED: Clean Firestore save — removed dead fields ---
    const handleSaveDraftsAndProceed = useCallback(async () => {
        const postsToSave = posts.filter(p => {
            if (!p.content) return false;
            if (typeof p.content === 'string') return p.content.trim().length > 0;
            return Object.values(p.content).some(v => v && v.trim().length > 0);
        });

        if (!userId || !projectId || !project || postsToSave.length === 0) {
            setError("Cannot save, required project data is missing.");
            return;
        }

        setLoading(true);
        try {
            const platformsRequired = mergedAdjustments?.platforms || ['facebook'];
            const batch = writeBatch(db);

            postsToSave.forEach(post => {
                const hasAllVisuals = platformsRequired.every(plat => {
                    const isCarousel = mergedAdjustments?.templates?.[plat]?.includes('carousel');
                    if (isCarousel) {
                        const seq = post.visualSequence?.[plat] || [];
                        return seq.length > 0 && seq.every(s => s.visual?.url);
                    }
                    const v = post.visual?.[plat] || (post.visual?.url ? post.visual : null);
                    return !!v?.url;
                });

                const postRef = doc(db, "projects", projectId, "posts", post.id);

                // Determine root template marker (for dashboard/filters)
                const tmplMap = mergedAdjustments?.templates || {};
                let rootTemplate = "standard";
                if (Object.values(tmplMap).some(t => t?.includes('carousel'))) rootTemplate = "carousel";
                else if (Object.values(tmplMap).some(t => t?.includes('reel'))) rootTemplate = "reel";
                else if (Object.values(tmplMap).some(t => t?.includes('story'))) rootTemplate = "story";

                batch.set(postRef, {
                    // Core identifiers
                    userId,
                    projectId,
                    contentPlanId,
                    // Content
                    subject: post.subject || "",
                    content: post.content || "",
                    // Visuals
                    visual: post.visual || null,
                    visualPrompt: post.visualSequence ? null : (post.visualPrompt || null),
                    visualSequence: post.visualSequence || null,
                    template: rootTemplate,
                    visualStatus: hasAllVisuals ? "ready" : "queued",
                    // Status
                    status: "draft",
                    // Timestamps
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    // Configuration (single source of truth — BE reads only)
                    adjustments: mergedAdjustments || {},
                    visualSettings: masterStyle || {},
                    // Creative brief — needed by visual pipeline (Architect + Synthesizer)
                    creativeBrief: creativeBrief || '',
                    // REMOVED: type (derivable from adjustments.templates)
                    // REMOVED: platform (read from project doc instead)
                    // REMOVED: targetPageId (read from project doc instead)
                    // REMOVED: targetInstagramId (read from project doc instead)
                });
            });
            await batch.commit();
            goToStep(WORKFLOW_STEPS.VISUALS);

            // Cleanup: posts are now in Firestore, draft is no longer needed
            cleanupDraft();
        } catch (err) {
            setError("Failed to save drafts. Please try again.");
            addToast('Failed to save drafts.', 'error');
        } finally {
            setLoading(false);
        }
    }, [userId, projectId, project, contentPlanId, posts, mergedAdjustments, masterStyle, creativeBrief, goToStep, addToast, cleanupDraft]);

    const onSchedulingComplete = useCallback(() => {
        navigate(`/project/${projectId}`, { replace: true, state: { message: `Content successfully scheduled!` } });
    }, [navigate, projectId]);

    if (loading && !project) return <div className="text-center p-10"><FiLoader className="inline mr-2 animate-spin text-slate-blue"/> <span className="text-cream/60">Loading...</span></div>;
    if (error && !project) return <div className="text-center p-8 text-soft-coral bg-soft-coral/10 p-4 rounded-3xl border border-soft-coral/20 shadow-lg"><FiAlertTriangle className="inline-block mr-2 text-xl mb-1"/>{error}</div>;
    if (!project && currentStep !== WORKFLOW_STEPS.MISSION_BRIEFING) return <div className="text-center p-10 text-cream/50">Project data could not be loaded.</div>;

    const renderCurrentStep = () => {
        switch (currentStep) {
            case WORKFLOW_STEPS.MISSION_BRIEFING:
                return (
                    <>
                        {selectedTrend && (
                            <div className="mb-4 px-5 py-3 bg-slate-blue/10 border border-slate-blue/20 rounded-2xl flex items-center gap-3">
                                <FiTrendingUp className="h-4 w-4 text-slate-blue shrink-0" />
                                <p className="text-xs text-cream/70">Creating from trend: <span className="font-bold text-cream">{selectedTrend.title}</span></p>
                            </div>
                        )}
                        <WorkflowTypeSelector project={project} onWorkflowSelect={handleWorkflowConfigSelect} selectedTrend={selectedTrend} />
                    </>
                );

            case WORKFLOW_STEPS.SPARK:
                return (
                    <InspirationSelector
                        project={project} onGenerateRefinedDescription={handleInspirationSubmit}
                        availablePillars={availablePillars} loading={loading}
                        onBack={() => goToStep(WORKFLOW_STEPS.MISSION_BRIEFING)}
                        workflowConfig={workflowConfig} onRefreshPillars={handleRefreshPillars}
                        selectedTrend={selectedTrend}
                    />
                );

            case WORKFLOW_STEPS.CLARIFICATION:
                return (
                    <ClarificationStep
                        questions={clarifyingQuestions}
                        onSubmit={(answers) => handleBriefGeneration(userSpark, inspirationType, answers)}
                        onSkip={() => handleBriefGeneration(userSpark, inspirationType, [])}
                        loading={loading} onBack={() => goToStep(WORKFLOW_STEPS.SPARK)}
                        project={project} workflowConfig={workflowConfig}
                    />
                );

            case WORKFLOW_STEPS.FINE_TUNE:
                return (
                    <PreGenerationAdjustments
                        creativeBrief={creativeBrief}
                        onAdjustmentsConfirm={handleAdjustmentsConfirm}
                        loading={loading}
                        onBack={() => goToStep(workflowConfig.workflow === 'stream' ? WORKFLOW_STEPS.CLARIFICATION : WORKFLOW_STEPS.SPARK)}
                        workflowConfig={workflowConfig}
                    />
                );

            case WORKFLOW_STEPS.PLAN:
                return (
                    <ContentPlanner
                        project={project} contentPlan={contentPlan}
                        postSubjects={posts.map(p => p.subject)}
                        onPlanConfirmed={handlePlanConfirmed}
                        loading={loading} onBack={() => goToStep(WORKFLOW_STEPS.FINE_TUNE)}
                        workflowConfig={workflowConfig}
                    />
                );

            case WORKFLOW_STEPS.PREVIEW:
                return (
                    <PostPreviewer
                        project={project} contentPlan={contentPlan} posts={posts}
                        onGeneratePosts={handleGenerateFinalContent}
                        onImprovePostElement={improvePostElementFn}
                        onEditPostText={handleEditPostText}
                        onConfirmPosts={handleSaveDraftsAndProceed}
                        loading={loading} adjustments={mergedAdjustments}
                        onBack={() => goToStep(workflowConfig.workflow === 'stream' ? WORKFLOW_STEPS.PLAN : WORKFLOW_STEPS.FINE_TUNE)}
                    />
                );

            case WORKFLOW_STEPS.VISUALS:
                return (
                    <VisualGenerator
                        project={project} posts={posts} workflowType={workflowConfig?.workflow}
                        onSetPostVisual={handleSetPostVisual}
                        onSetPostPrompt={handleSetPostPrompt}
                        onVisualsConfirmed={() => goToStep(WORKFLOW_STEPS.PUBLISH)}
                        onSettingsChange={setMasterStyle}
                        loading={loading} adjustments={mergedAdjustments}
                        onBack={() => goToStep(WORKFLOW_STEPS.PREVIEW)}
                    />
                );

            case WORKFLOW_STEPS.PUBLISH:
                return (
                    <Scheduler
                        project={project} posts={posts} workflowConfig={workflowConfig}
                        contentPlanId={contentPlanId} provisionalStreamId={provisionalStreamId}
                        adjustments={mergedAdjustments} visualSettings={masterStyle}
                        onSchedulingComplete={onSchedulingComplete}
                        onBack={() => goToStep(WORKFLOW_STEPS.VISUALS)}
                        loading={loading}
                    />
                );
            default:
                return <p>Invalid workflow step.</p>;
        }
    };

    return (
        <div className="space-y-6 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10 text-cream">
            <WorkflowHeader
                project={project}
                currentStepTitle={STEP_TITLE_MAP[currentStep] || ''}
                currentStepNumber={getStepDisplayNumber(currentStep, workflowConfig?.workflow || 'single')}
                onExit={() => navigate(`/project/${projectId}`)}
            />

            {/* Progress Stepper — shown after Mission Briefing */}
            {workflowConfig && currentStep !== WORKFLOW_STEPS.MISSION_BRIEFING && (
                <WorkflowStepper
                    currentStepKey={currentStep}
                    workflowType={workflowConfig.workflow}
                />
            )}

            <div className="min-h-[20px] text-center space-y-1">
                {error && <p className="text-sm text-soft-coral flex items-center justify-center font-bold px-4 py-2 bg-soft-coral/10 rounded-full inline-block border border-soft-coral/20"><FiAlertTriangle className="mr-2" />{error}</p>}
                {infoMessage && <p className="text-sm text-slate-blue flex items-center justify-center font-bold animate-pulse px-4 py-2 bg-slate-blue/10 rounded-full inline-block border border-slate-blue/20"><FiInfo className="mr-2" />{infoMessage}</p>}
                {loading && !infoMessage && !error && currentStep !== WORKFLOW_STEPS.MISSION_BRIEFING && <p className="text-sm text-slate-blue flex items-center justify-center font-bold"><FiLoader className="mr-2 animate-spin"/> Processing...</p>}
            </div>
            {renderCurrentStep()}
        </div>
    );
}

ContentWorkflow.propTypes = {
    projectId: PropTypes.string.isRequired,
    resumeDraftId: PropTypes.string,
};

export default ContentWorkflow;
